import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { verifyPayment, getReservationWithPayment } from '../services/paymentService';
import { shouldRedirectToKikis, useKikisProfileRedirect } from '../utils/kikisRedirect';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [error, setError] = useState('');

  const reservationId = searchParams.get('reservationId');

  // Kiki's bookings stay on this confirmation for a moment, then bounce back to
  // the Kiki's profile page. Other restaurants are unaffected.
  const kikisRedirecting = useKikisProfileRedirect(
    paymentStatus === 'paid' && shouldRedirectToKikis(restaurantId),
  );

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!reservationId) {
        setError('No reservation found');
        setLoading(false);
        return;
      }

      try {
        // Verify payment status
        const verifyResponse = await verifyPayment(reservationId);

        // Kiki's bookings show this confirmation briefly, then redirect to the
        // Kiki's profile page (handled by useKikisProfileRedirect above).
        setRestaurantId(verifyResponse?.restaurantId ?? null);
        setPaymentStatus(verifyResponse.paymentStatus);

        // Get full reservation details
        try {
          const reservationData = await getReservationWithPayment(reservationId);
          setReservation(reservationData);
        } catch (resErr) {
          // Reservation fetch may fail if user not logged in, but we still have payment status
          console.log('Could not fetch reservation details:', resErr);
        }

        setLoading(false);
      } catch (err) {
        console.error('Payment verification error:', err);
        setError('Unable to verify payment status. Please check My Reservations.');
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [reservationId]);

  const handleRetryPayment = () => {
    // Navigate back to my reservations where they can retry
    navigate('/my-reservations');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="payment-success-container">
          <div className="loading-card">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h2>Verifying Payment</h2>
            <p>Please wait while we confirm your payment...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="payment-success-container">
          <div className="status-card error-card">
            <div className="status-icon error-icon">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>
            <h2>Something Went Wrong</h2>
            <p>{error}</p>
            <div className="action-buttons">
              <button className="btn-primary-action" onClick={() => navigate('/my-reservations')}>
                <i className="bi bi-calendar-check me-2"></i>
                View My Reservations
              </button>
              <button className="btn-secondary-action" onClick={() => navigate('/')}>
                <i className="bi bi-house me-2"></i>
                Back to Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Payment successful
  if (paymentStatus === 'paid') {
    return (
      <>
        <Navbar />
        <div className="payment-success-container">
          <div className="status-card success-card">
            <div className="status-icon success-icon">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <h2>Payment Successful!</h2>
            <p>Your reservation has been confirmed.</p>

            {kikisRedirecting && (
              <div className="confirmation-message">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Taking you back to Kiki's…
              </div>
            )}

            {reservation && (
              <div className="booking-details">
                <h3>Booking Details</h3>
                <div className="details-grid">
                  {reservation.restaurant && (
                    <div className="detail-item">
                      <i className="bi bi-building"></i>
                      <div>
                        <span className="detail-label">Restaurant</span>
                        <span className="detail-value">{reservation.restaurant.name}</span>
                      </div>
                    </div>
                  )}
                  <div className="detail-item">
                    <i className="bi bi-calendar3"></i>
                    <div>
                      <span className="detail-label">Date</span>
                      <span className="detail-value">{formatDate(reservation.date)}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <i className="bi bi-clock"></i>
                    <div>
                      <span className="detail-label">Time</span>
                      <span className="detail-value">{formatTime(reservation.time)}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <i className="bi bi-people"></i>
                    <div>
                      <span className="detail-label">Guests</span>
                      <span className="detail-value">{reservation.guests} {reservation.guests === 1 ? 'Guest' : 'Guests'}</span>
                    </div>
                  </div>
                  {reservation.paymobAmountCents && (
                    <div className="detail-item">
                      <i className="bi bi-credit-card"></i>
                      <div>
                        <span className="detail-label">Amount Paid</span>
                        <span className="detail-value">EGP {(reservation.paymobAmountCents / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  <div className="detail-item">
                    <i className="bi bi-hash"></i>
                    <div>
                      <span className="detail-label">Confirmation #</span>
                      <span className="detail-value">RES-{reservation.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="confirmation-message">
              <i className="bi bi-envelope-check me-2"></i>
              A confirmation email has been sent to your email address.
            </div>

            <div className="action-buttons">
              <button className="btn-primary-action" onClick={() => navigate('/my-reservations')}>
                <i className="bi bi-calendar-check me-2"></i>
                View My Reservations
              </button>
              {/* "Make Another Booking" (→ home/all restaurants) removed so a finished
                  payment doesn't redirect the guest out to the front-end. */}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Payment pending
  if (paymentStatus === 'pending') {
    return (
      <>
        <Navbar />
        <div className="payment-success-container">
          <div className="status-card pending-card">
            <div className="status-icon pending-icon">
              <i className="bi bi-hourglass-split"></i>
            </div>
            <h2>Payment Processing</h2>
            <p>Your payment is still being processed. This may take a few moments.</p>

            <div className="pending-info">
              <i className="bi bi-info-circle me-2"></i>
              Please do not close this page. Your reservation will be confirmed once payment is complete.
            </div>

            <div className="action-buttons">
              <button className="btn-primary-action" onClick={() => window.location.reload()}>
                <i className="bi bi-arrow-clockwise me-2"></i>
                Refresh Status
              </button>
              <button className="btn-secondary-action" onClick={() => navigate('/my-reservations')}>
                <i className="bi bi-calendar-check me-2"></i>
                Check Later
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Payment failed
  return (
    <>
      <Navbar />
      <div className="payment-success-container">
        <div className="status-card failed-card">
          <div className="status-icon failed-icon">
            <i className="bi bi-x-circle-fill"></i>
          </div>
          <h2>Payment Failed</h2>
          <p>Unfortunately, your payment could not be processed.</p>

          <div className="failed-info">
            <p>This could be due to:</p>
            <ul>
              <li>Insufficient funds</li>
              <li>Card declined by your bank</li>
              <li>Network connectivity issues</li>
              <li>Incorrect card details</li>
            </ul>
          </div>

          <div className="action-buttons">
            <button className="btn-primary-action" onClick={handleRetryPayment}>
              <i className="bi bi-arrow-repeat me-2"></i>
              Try Again
            </button>
            <button className="btn-secondary-action" onClick={() => navigate('/')}>
              <i className="bi bi-house me-2"></i>
              Back to Home
            </button>
          </div>

          <div className="support-info">
            <i className="bi bi-headset me-2"></i>
            Need help? Contact our support team.
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PaymentSuccess;
